import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { CollectionSlider } from '@components';
import { useCollectionsQuery } from '@utils/react-query';
import { useActiveFrame, useTranslation } from '@utils/hooks';
import { BASE_FRAME_NAMES, LOCALE_DICT, TOP_FRAMES_PATH } from '@constants';
import { COLLECTION_LISTS } from '@constants/contentful';
import { evaluateEllaFrame } from '@utils/strings';

type FeaturedCollectionSliderProps = {
	header: string;
	subheader: string;
	collectionSlug?: string;
	iconType?: 'heart' | 'star' | 'clock';
};

const getTranslatedTexts = translator => {
	return {
		collectionTitle: (collectionTitle: string) => translator('collection-title', { collectionTitle }),
	};
};

const FeaturedCollectionSlider = ({
	header,
	subheader,
	collectionSlug = COLLECTION_LISTS.LEAVING_SOON,
	iconType = 'clock',
	...rest
}: FeaturedCollectionSliderProps) => {
	const isEllaRename = useFeatureIsOn('is-ella-rename');
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	//@ts-ignore
	const safeSlug = rest?.collectionList?.slug || collectionSlug;
	const [activeFrame] = useActiveFrame();
	const [frameShape] = useState<(typeof BASE_FRAME_NAMES)[number]>(activeFrame);
	const { locale } = useRouter();
	const { countryCode: country, languageCode: language } = LOCALE_DICT[locale];
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	const { data: collections, isLoading } = useCollectionsQuery(safeSlug, {
		queryKey: safeSlug,
		type: 'buildflow',
		withProducts: true,
		skipVariants: true,
		includeSpecificFrameVariant: true,
		selectedOptions: [{ name: 'Frame', value: evaluateEllaFrame(frameShape, isEllaRename) }],
		country,
		language,
		queryRefreshVars: [locale, `${isEllaRename}`],
	});

	const products = useMemo(() => {
		if (collections) {
			const collection = Object.values(collections)[0];
			const { products: variants } = collection;
			return variants.map(variant => {
				return {
					...variant.product,
					variants: [variant],
				};
			});
		}
	}, [collections]);

	if (!collections && !isLoading) return null;

	const collectionInfo = collections
		? {
				backgroundUrl: collections[0].image?.url,
				title: translations.collectionTitle(collections[0].title),
				link: `${TOP_FRAMES_PATH}/${collections[0].handle}`,
			}
		: null;

	return (
		<CollectionSlider
			iconType={iconType}
			header={header}
			subHeader={subheader}
			products={products}
			limit={8}
			featuredCollection={collectionInfo}
			isDataLoading={!collections || isLoading}
			sliderType={'leaving-soon'}
			collectionHandle={collections && collections[0].handle}
			{...rest}
		/>
	);
};

export default FeaturedCollectionSlider;
